import { FC, useState } from 'react'
import { SectionCaseStudyDetailsProps } from '@portfolio/models'
import { styled, useMediaQuery } from '@portfolio/styles'
import {
  Button,
  Container,
  Link,
  Modal,
  PortableText,
  SectionWrapper,
} from '@portfolio/components'
import { useTranslation } from 'react-i18next'
import TechStackModal from './TechStackModal'
import { OpenNewTab } from '@portfolio/icons'
import { SectionQuote } from '../SectionQuote/SectionQuote'
import { SectionIconWithTextList } from '../SectionIconWithTextList/SectionIconWithTextList'
import { SectionSingleMediaWithDescription } from '../SectionSingleMediaWithDescription/SectionSingleMediaWithDescription'
import { SectionVideoPlayer } from '../SectionVideoPlayer/SectionVideoPlayer'
import { SectionQuoteWithVideo } from '../SectionQuoteWithVideo/SectionQuoteWithVideo'
import { SectionStats } from '../SectionStats/SectionStats'

const StyledSectionWrapper = styled(SectionWrapper, {
  display: 'grid',
  gap: '$72',

  marginTop: '$56',
  marginBottom: '$56',

  '@lg': {
    gap: 'unset',
    gridTemplateColumns: '1fr 2fr .25fr',

    marginTop: '$144',
    marginBottom: '$144',
  },
})

const StyledDetailsWrapper = styled(Container, {
  maxWidth: '$aboutPageLeftColumnMaxWidth',
  '@lg': {
    gridColumn: '1/2',
  },

  '> div:not(:last-child)': {
    marginBottom: '$40',
  },
})

const StyledIntroductionWrapper = styled(Container, {
  projectFont: 'body01',
  h2: {
    projectFont: 'heading07',
  },
  '@lg': {
    gridColumn: '2/span 1',
    maxWidth: '$richTextWidth',
  },
})

const StyledDetailsTitle = styled('h2', {
  projectFont: 'ui03',
  textTransform: 'uppercase',
})

const StyledDetailsText = styled('p', {
  projectFont: 'body02',
  display: 'inline-block',
})

const StyledIcon = styled('span', {
  paddingLeft: '$4',
})

const StyledOpenModalButton = styled(Button, {
  projectFont: 'body02',
  textDecoration: 'underline',
  textUnderlineOffset: '5px',
  textDecorationThickness: '1px',
})

export const SectionCaseStudyDetails: FC<SectionCaseStudyDetailsProps> = ({
  introduction,
  techStack,
  design,
  url,
}) => {
  const [isTechStackModalOpen, setTechStackModalOpen] = useState(false)
  const isMd = useMediaQuery('md')
  const { t } = useTranslation('caseStudy')

  const closeTechStackModal = () => {
    setTechStackModalOpen(false)
  }

  const openTechStackModal = () => {
    setTechStackModalOpen(true)
  }

  return (
    <StyledSectionWrapper type="NO_MARGIN">
      <StyledDetailsWrapper>
        {url && (
          <div>
            <StyledDetailsTitle>{t('liveSite')}</StyledDetailsTitle>
            <Link href={url} target="_blank">
              <StyledDetailsText>{url} </StyledDetailsText>
              <StyledIcon>
                <OpenNewTab fill="black" />
              </StyledIcon>
            </Link>
          </div>
        )}
        {design && (
          <div>
            <StyledDetailsTitle>{t('digitalDesign')}</StyledDetailsTitle>
            <StyledDetailsText>{design}</StyledDetailsText>
          </div>
        )}

        {techStack && techStack.length > 0 && (
          <>
            <div>
              <StyledDetailsTitle>{t('techStack')}</StyledDetailsTitle>
              {techStack.map((tech, index) => (
                <StyledDetailsText key={`${index}-${tech.name}`}>
                  {tech.name}
                  <span>,&nbsp;</span>
                </StyledDetailsText>
              ))}
              <StyledOpenModalButton onClick={openTechStackModal}>
                {t('more')}
              </StyledOpenModalButton>
            </div>

            <Modal
              preventScroll={true}
              isOpen={isTechStackModalOpen}
              size={isMd ? 'intrinsic' : 'stretch'}
              variant={isMd ? 'rounded' : 'default'}
              onRequestClose={closeTechStackModal}
            >
              <TechStackModal
                closeModal={closeTechStackModal}
                techStack={techStack}
              />
            </Modal>
          </>
        )}
      </StyledDetailsWrapper>
      {introduction && (
        <StyledIntroductionWrapper>
          <PortableText
            value={introduction}
            // this section has explicit top and bottom margins so we disable them on PortableText. Section is display: grid, so margin collapsing doesn't work anyway.
            disableTopBottomMargins={true}
            additionalComponents={{
              types: {
                sectionQuote: (props) => {
                  return <SectionQuote {...props.value} />
                },
                sectionIconWithTextList: (props) => {
                  return <SectionIconWithTextList {...props.value} />
                },
                sectionSingleMediaWithDescription: (props) => {
                  return <SectionSingleMediaWithDescription {...props.value} />
                },
                sectionVideoPlayer: (props) => {
                  return <SectionVideoPlayer {...props.value} />
                },
                sectionQuoteWithVideo: (props) => {
                  return <SectionQuoteWithVideo {...props.value} />
                },
                sectionStats: (props) => {
                  return <SectionStats {...props.value} />
                },
              },
            }}
          />
        </StyledIntroductionWrapper>
      )}
    </StyledSectionWrapper>
  )
}
